import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getAllStockItems } from '@/lib/queries/stockItems'
import { getAllCategories } from '@/lib/queries/categories'

/**
 * Stock Items List page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: stock-items-list.html
 */
export default function StockItemsList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  
  // Initialize selectedCategories from URL params
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(() => {
    const categoriesParam = searchParams.get('categories')
    if (categoriesParam) {
      return new Set(categoriesParam.split(',').filter(Boolean))
    }
    return new Set()
  })
  
  // Sync selectedCategories to URL params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams)
    const currentCategories = newParams.get('categories')
    const newCategories = selectedCategories.size > 0 
      ? Array.from(selectedCategories).join(',')
      : ''
    
    // Only update if changed to avoid infinite loops
    if (currentCategories !== newCategories) {
      if (selectedCategories.size > 0) {
        newParams.set('categories', newCategories)
      } else {
        newParams.delete('categories')
      }
      setSearchParams(newParams, { replace: true })
    }
  }, [selectedCategories]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: stockItems, isLoading } = useQuery({
    queryKey: ['stockItems'],
    queryFn: getAllStockItems,
  })

  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories,
  })

  // Build filter categories from fetched categories
  const filterCategories = useMemo(() => {
    const allOption = { id: 'all', label: 'All' }
    if (!categories || categories.length === 0) {
      return [allOption]
    }
    // Build filter categories - use code if available, otherwise use _id as fallback
    const categoryMap = new Map<string, { id: string; label: string; code?: string }>()
    
    categories.forEach((cat) => {
      // Use code if available, otherwise use _id as fallback
      const categoryId = cat.code 
        ? cat.code.toLowerCase() 
        : cat._id.toLowerCase()
      
      // Only add if not already in map (handle duplicates)
      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          id: categoryId,
          label: cat.name,
          code: cat.code,
        })
      }
    })
    
    return [
      allOption,
      ...Array.from(categoryMap.values()),
    ]
  }, [categories])

  // Helper to get supplier item name from supplierItem
  const getSupplierName = (supplierItem: any): string | undefined => {
    if (!supplierItem || typeof supplierItem !== 'object') return undefined
    // If it's a reference object, return undefined
    if ('_ref' in supplierItem && !('name' in supplierItem)) return undefined
    // If it has name property, it's populated
    if ('name' in supplierItem && typeof supplierItem.name === 'string') {
      return supplierItem.name
    }
    // Fallback to SKU if name not available
    if ('sku' in supplierItem && typeof supplierItem.sku === 'string') {
      return supplierItem.sku
    }
    return undefined
  }

  // Helper to get category name from category
  const getCategoryName = (category: any): string | undefined => {
    if (!category || typeof category !== 'object') return undefined
    // If it's a reference object, return undefined
    if ('_ref' in category && !('name' in category)) return undefined
    // If it has name property, it's populated
    if ('name' in category) return category.name
    return undefined
  }

  // Helper to get category code from category (for filtering)
  // Returns code if available, otherwise _id as fallback
  const getCategoryCode = (category: any): string | undefined => {
    if (!category || typeof category !== 'object') return undefined
    // If it's a reference object, return undefined
    if ('_ref' in category && !('code' in category) && !('_id' in category)) return undefined
    // If it has code property, use code
    if ('code' in category && category.code) return category.code
    // Fallback to _id if code is not available
    if ('_id' in category && category._id) return category._id
    return undefined
  }

  // Filter and search stock items
  const filteredItems = useMemo(() => {
    if (!stockItems) return []

    return stockItems.filter((item) => {
      // Search filter
      const supplierName = getSupplierName(item.supplierItem)
      const itemName = item.name?.toLowerCase() || ''
      const itemSku = item.sku?.toLowerCase() || ''
      const searchLower = searchQuery.toLowerCase()

      const matchesSearch =
        searchQuery === '' ||
        itemName.includes(searchLower) ||
        itemSku.includes(searchLower) ||
        supplierName?.toLowerCase().includes(searchLower)

      // Category filter - if no categories selected, show all items
      if (selectedCategories.size === 0) {
        return matchesSearch
      }

      const categoryCode = getCategoryCode(item.category)
      if (!categoryCode) {
        return false // If item has no category and filters are set, exclude it
      }
      
      // Normalize category code for comparison
      const normalizedCategoryCode = categoryCode.toLowerCase()
      
      // Check if item's category is in the selected categories set
      const matchesCategory = selectedCategories.has(normalizedCategoryCode)
      

      return matchesSearch && matchesCategory
    })
  }, [stockItems, searchQuery, selectedCategories])

  const getCategoryLabel = (category: any) => {
    const categoryName = getCategoryName(category)
    if (categoryName) return categoryName
    
    const categoryCode = getCategoryCode(category)
    if (categoryCode) {
      // Try to find matching category from fetched categories
      const matchingCategory = categories?.find((cat) => cat.code === categoryCode)
      if (matchingCategory) return matchingCategory.name
      
      // Fallback to filterCategories if available
      return filterCategories.find((f) => f.id === categoryCode.toLowerCase())?.label || categoryCode
    }
    
    return 'Item'
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Stock Items
            {stockItems && (
              <span className="text-lg font-normal text-gray-500 ml-2">
                ({filteredItems.length})
              </span>
            )}
          </h1>

          {/* Search Bar */}
          <input
            type="search"
            placeholder="Search stock items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Filter Chips */}
          <div className="flex gap-2 overflow-x-auto mt-3 pb-2 -webkit-overflow-scrolling-touch">
            {isLoadingCategories ? (
              <div className="text-sm text-gray-500 px-4 py-2">Loading categories...</div>
            ) : filterCategories.length > 1 ? (
              filterCategories.map((filter) => {
                let isSelected: boolean
                if (filter.id === 'all') {
                  isSelected = selectedCategories.size === 0
                } else {
                  // Ensure we're comparing normalized codes
                  isSelected = selectedCategories.has(filter.id.toLowerCase())
                }
                
                const handleClick = () => {
                  if (filter.id === 'all') {
                    // Clear all filters
                    setSelectedCategories(new Set())
                  } else {
                    // Toggle this category - use normalized code
                    const normalizedId = filter.id.toLowerCase()
                    const newSelected = new Set(selectedCategories)
                    if (newSelected.has(normalizedId)) {
                      newSelected.delete(normalizedId)
                    } else {
                      newSelected.add(normalizedId)
                    }
                    setSelectedCategories(newSelected)
                  }
                }
                
                return (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={handleClick}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44
                      transition-colors
                      ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                    aria-pressed={isSelected}
                  >
                    {filter.label}
                  </button>
                )
              })
            ) : filterCategories.length === 1 ? (
              <div className="text-sm text-gray-500 px-4 py-2">
                No categories available. Please create categories (product, packaging, shipping) in Sanity.
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchQuery || selectedCategories.size > 0
                ? 'No stock items match your filters'
                : 'No stock items found'}
            </p>
            <Link
              to="/workflows/stock-item"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium touch-target-min-44"
            >
              Add Stock Item
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <Link
                key={item._id}
                to={`/stock-items/${item._id}`}
                className="block bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow touch-target-min-44"
              >
                <div className="flex gap-3">
                  {/* Item Image */}
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 text-3xl">
                    {item.images && item.images.length > 0 ? (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <span>📦</span>
                    )}
                  </div>

                  {/* Item Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">
                      {item.name}
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">SKU: {item.sku}</p>

                    {/* Badges */}
                    <div className="flex gap-2 flex-wrap mb-2">
                      {item.category && getCategoryName(item.category) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            const categoryCode = getCategoryCode(item.category)
                            if (categoryCode) {
                              // Normalize the category code
                              const normalizedCode = categoryCode.toLowerCase()
                              const newSelected = new Set(selectedCategories)
                              // Toggle this category
                              if (newSelected.has(normalizedCode)) {
                                newSelected.delete(normalizedCode)
                              } else {
                                newSelected.add(normalizedCode)
                              }
                              setSelectedCategories(newSelected)
                            }
                          }}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded hover:bg-green-200 transition-colors touch-target-min-44"
                        >
                          {getCategoryLabel(item.category)}
                        </button>
                      )}
                      {getSupplierName(item.supplierItem) && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          {getSupplierName(item.supplierItem)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Floating Action Button (FAB) */}
      <Link
        to="/workflows/stock-item"
        className="fixed bottom-24 right-4 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center text-2xl font-bold z-50 touch-target-min-44 hover:bg-blue-700 transition-colors"
        aria-label="Add Stock Item"
      >
        +
      </Link>
    </div>
  )
}
